import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { GCP_PROJECT_ID_PATTERN } from "./gsa-identity.js";
import { BLOB_BUCKETS_PER_APP_MAX, BLOB_BUCKET_DURABILITIES, BLOB_BUCKET_LOCATION_PATTERN, PHYSICAL_BUCKET_NAME_PATTERN, RESOURCE_SLUG_PATTERN, blobBucketModeSchema, blobBucketNameSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/blob-bindings.ts
/**
* Blob bucket bindings: the CP-to-DP desired state and the pod bindings file
* (2026-10-13-blob-buckets, stage 1 contracts, D13, D14; stage 2c, R-060).
*
* The chain:
*
* 1. The operator program reports each org's adoption state of each app in the status report
*    member `StatusReportSchema.appAdoptions` (2026-10-14-aws-dp-parity D3). release-engine stores
*    the rows in `release.dp_app_adoptions`, and the allocator reads the rows with
*    `state = 'accepted'` to decide which org gets a bucket (D14, R-060 item 2).
* 2. release-engine (WS-4) allocates one physical bucket per (data plane, org, app, logical bucket)
*    and writes an {@link AppBlobBindings} per app into the desired state, `ready` rows only.
* 3. The operator program (WS-5) renders one ConfigMap per fixture with a {@link BlobBindingsFile}
*    that holds the buckets the fixture binds, each with the mode the fixture asked for. It mounts
*    the file read-only at {@link BLOB_BINDINGS_MOUNT_PATH} and sets {@link BLOB_BINDINGS_FILE_ENV}.
* 4. The SDK handle (WS-6, WS-8, WS-9) reads the file and resolves a logical name and the org of
*    the request to one physical bucket, by the rules of {@link resolveBlobBinding}.
*
* Two org id spaces (R-060 item 3). Each is a lower-case UUID, so one key grammar serves both:
*
* - The `orgs` keys of an {@link AppBlobBindings} in the desired state are CP org ids
*   (`platform.organizations.id`), as the keys of `release.platform_buckets` are (R-028).
* - The `orgs` keys of a {@link BlobBindingsFile} in the pod are DP-local org ids
*   (`auth.organizations.id`), the id an app holds at each request. The operator program maps
*   each CP key with `ResidentOrgDirectory.dpOrgIdFor` when it writes the file. It leaves out an
*   org with no pairing (defaults row F3).
*
* The shared fixtures `cloud/platform-sdk/fixtures/blob-bindings/` hold valid and invalid files
* and the expected resolutions; the node, Python and Rust SDK tests read them.
*
* Pure: no clock, no filesystem, no network.
*/
/** The env var that names the bindings file inside a fixture pod. */
const BLOB_BINDINGS_FILE_ENV = "ZION_BLOB_BINDINGS_FILE";
/**
* The path the operator program mounts the bindings file at, read-only (ruling R-008).
*
* The path sits under `/etc/zion`, and never under `/var/run/zion`. A fixture pod with
* `coreApiAccess`, `orgCrypto` or `audit` mounts the signer's `/var/run/zion` (`SIGNER_MOUNT_PATH`
* of the operator program) read-only into the app container. The operator program's
* `assertNoUnsafeMountNesting` refuses a mount nested under a read-only mount of the same
* container. A DP app container mounts nothing at `/etc/zion` before this feature. The Node,
* Python and Rust SDKs carry the same literal.
*/
const BLOB_BINDINGS_MOUNT_PATH = "/etc/zion/blob-bindings/bindings.json";
/** The one version of {@link BlobBindingsFile}. A reader refuses every other value. */
const BLOB_BINDINGS_FILE_VERSION = 1;
/**
* The providers a physical bucket can have. `gcs`: Google Cloud Storage. `s3`: Amazon S3.
* `s3-compatible`: an S3 API at an `endpoint`, such as SeaweedFS on a local data plane.
*/
const BLOB_BUCKET_PROVIDERS = [
	"gcs",
	"s3",
	"s3-compatible"
];
/**
* The most orgs one logical bucket binds in one message. A refusal backstop on a CP-written and an
* operator-written map: the producer never writes more, and a parse refuses a larger map. The
* Python and Rust readers of the pod file carry the same figure.
*
* The live bound is lower. The desired-state member holds at most
* {@link APP_BLOB_BINDINGS_MAX_BYTES} bytes. The shortest org entry, an `s3` entry with the
* location `us` and the region `us`, is 131 bytes with its comma. So one logical bucket holds at
* most 7,557 orgs, and each of 16 logical buckets at most 472 orgs. The operator program writes a pod file from that member and leaves out each org with
* no pairing, so a pod file holds at most as many orgs as its member.
*
* So this figure stays the backstop of the pod file readers. A member can never reach it.
*/
const BLOB_BINDINGS_ORGS_MAX = 1e4;
/**
* The most bytes of one {@link AppBlobBindings} member as compact JSON (`JSON.stringify` with no
* spacing): 990,000. {@link appBlobBindingsSchema} refuses a larger member, with the message
* `at most 990000 bytes of compact JSON`. Every string of the member is ASCII by its grammar, so
* the string length is the UTF-8 byte count.
*
* Why this figure. The operator program writes each fixture's {@link BlobBindingsFile} into one
* ConfigMap, and it refuses a file over 1,000,000 bytes (`BLOB_BINDINGS_FILE_MAX_BYTES` of the
* operator program), because Kubernetes refuses a ConfigMap over 1 MiB. A file holds a subset of
* the member's buckets with the same `orgs`, so it is at most 6 bytes longer than the member:
*
* 1. The file root `{"version":1,"app":…` is 8 bytes longer than the member root `{"appSlug":…`.
* 2. Each bucket entry of the file is at least 2 bytes shorter: `"mode":"readWrite"` (18 bytes)
*    replaces `"durability":"cache"` (20 bytes) or `"durability":"durable"` (22 bytes).
* 3. A file holds at least one bucket, and a sort of the keys changes no length.
*
* So every file that the operator renders from a member that parses is at most 990,006 bytes, and
* the operator's size refusal never fires for it. The contract states no byte bound on the pod
* file itself: a ConfigMap cannot carry a file over 1 MiB, so a reader never sees one.
*
* The producer rule (WS-4). The producer must keep each member within this bound before it writes
* it. A member over the bound fails {@link appBlobBindingsSchema}, the producer then refuses its
* own projection, and the entry omits the member (ruling R-010): the operator removes every
* bindings file of the app. So the allocator must stop before the bound. It allocates a bucket for
* a new org only when the member, with a binding for that org in each logical bucket of the app,
* stays within this bound. It counts each live row (`pending` or `ready`) as if it were `ready`.
*/
const APP_BLOB_BINDINGS_MAX_BYTES = 99e4;
/** The longest S3 endpoint, in characters. */
const BLOB_BUCKET_ENDPOINT_MAX_LENGTH = 270;
/**
* The grammar of an S3 endpoint: `http://` or `https://`, a lowercase host of letters, numbers,
* dots and hyphens, and an optional port. No path, no query, no user info. `http` is legal because
* the local SeaweedFS endpoint is an in-cluster address with no TLS.
*/
const BLOB_BUCKET_ENDPOINT_PATTERN = /^https?:\/\/[a-z0-9](?:[a-z0-9.-]{0,251}[a-z0-9])?(?::[0-9]{1,5})?$/;
/** The longest app slug: the `appSlug` cap of the `App` XRD. */
const APP_SLUG_MAX_LENGTH = 64;
const bucketField = z.string().regex(PHYSICAL_BUCKET_NAME_PATTERN, "a physical bucket name from physicalBucketName");
const locationField = z.string().max(31).regex(BLOB_BUCKET_LOCATION_PATTERN, "a lowercase location such as us-central1");
/**
* One physical bucket, discriminated on `provider`. Strict in every arm.
*
* - `gcs`: `bucket`, `location` and `project` (a GCP project id). No `region`, no `endpoint`.
* - `s3`: `bucket`, `location` and `region` (the S3 API region). No `project`, no `endpoint`.
* - `s3-compatible`: `bucket`, `location`, `endpoint` ({@link BLOB_BUCKET_ENDPOINT_PATTERN}) and an
*   optional `region`. No `project`.
*
* `bucket` matches `PHYSICAL_BUCKET_NAME_PATTERN`: every physical name comes from
* `physicalBucketName` (defaults row F10). `location` is the bucket location in
* `BLOB_BUCKET_LOCATION_PATTERN`, at most 31 characters.
*/
const blobBucketPhysicalSchema = z.discriminatedUnion("provider", [
	z.object({
		provider: z.literal("gcs"),
		bucket: bucketField,
		location: locationField,
		project: z.string().max(30).regex(GCP_PROJECT_ID_PATTERN, "a GCP project id")
	}).strict(),
	z.object({
		provider: z.literal("s3"),
		bucket: bucketField,
		location: locationField,
		region: locationField
	}).strict(),
	z.object({
		provider: z.literal("s3-compatible"),
		bucket: bucketField,
		location: locationField,
		endpoint: z.string().max(270).regex(BLOB_BUCKET_ENDPOINT_PATTERN, "an http or https endpoint with no path"),
		region: locationField.optional()
	}).strict()
]);
const orgIdSchema = z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID org id");
const appSlugSchema = z.string().min(2).max(APP_SLUG_MAX_LENGTH).regex(RESOURCE_SLUG_PATTERN, "an app slug");
/**
* A map of org id to physical bucket, at most {@link BLOB_BINDINGS_ORGS_MAX} entries. The key is a
* CP org id in the desired state and a DP-local org id in the pod file (R-060 item 3).
*/
const orgsSchema = z.record(orgIdSchema, blobBucketPhysicalSchema).refine((orgs) => Object.keys(orgs).length <= BLOB_BINDINGS_ORGS_MAX, { message: `at most ${BLOB_BINDINGS_ORGS_MAX} orgs` });
/** A map of logical name to entry, 1 to `BLOB_BUCKETS_PER_APP_MAX` entries. */
function bucketMap(entry) {
	return z.record(blobBucketNameSchema, entry).refine((buckets) => Object.keys(buckets).length >= 1, { message: "at least one bucket" }).refine((buckets) => Object.keys(buckets).length <= BLOB_BUCKETS_PER_APP_MAX, { message: `at most ${BLOB_BUCKETS_PER_APP_MAX} buckets` });
}
/**
* The two scope arms of one bucket entry, discriminated on `scope`, both strict. `org`: `orgs`
* maps org ids to physical buckets, and the entry has no `global` key. `global`: `global`
* is the one shared bucket of every consenting org (D7), and `orgs` is the empty object.
* `extra` adds the members the two arms share.
*/
function scopedEntry(extra) {
	return z.discriminatedUnion("scope", [z.object({
		...extra,
		scope: z.literal("org"),
		orgs: orgsSchema
	}).strict(), z.object({
		...extra,
		scope: z.literal("global"),
		orgs: z.object({}).strict(),
		global: blobBucketPhysicalSchema
	}).strict()]);
}
/**
* The bindings of one logical bucket in the desired state, in the two scope arms of
* {@link scopedEntry}. `durability` comes from the app declaration. In the `org` arm, `orgs` holds
* one physical bucket per CP org id whose binding row is `ready`; an org with no ready row is
* absent (R-060 item 3).
*/
const appBlobBucketBindingSchema = scopedEntry({ durability: z.enum(BLOB_BUCKET_DURABILITIES) });
/**
* The blob bindings of one app in the desired state (WS-2 places it, WS-4 writes it, WS-5 reads
* it). `appSlug` is the app slug. `buckets` maps each logical name the app declares to its
* {@link AppBlobBucketBinding}, 1 to `BLOB_BUCKETS_PER_APP_MAX` entries. Every `orgs` key is a CP
* org id (R-060 item 3). Strict.
*
* The member as compact JSON holds at most {@link APP_BLOB_BINDINGS_MAX_BYTES} bytes, so every pod
* file rendered from it fits in one ConfigMap. The byte check runs only on a member that passes
* every other check, so a member over a count cap fails with the count message alone.
*/
const appBlobBindingsSchema = z.object({
	appSlug: appSlugSchema,
	buckets: bucketMap(appBlobBucketBindingSchema)
}).strict().refine((bindings) => JSON.stringify(bindings).length <= APP_BLOB_BINDINGS_MAX_BYTES, {
	message: `at most ${APP_BLOB_BINDINGS_MAX_BYTES} bytes of compact JSON`,
	when: (payload) => payload.issues.length === 0
});
/**
* One logical bucket of the pod file, in the two scope arms of {@link scopedEntry}, with the
* `mode` the fixture bound it with. Every `orgs` key is a DP-local org id (R-060 item 3).
*/
const blobBindingsFileBucketSchema = scopedEntry({ mode: blobBucketModeSchema });
/**
* The bindings file the operator program mounts into each fixture pod (D13): `version` is
* {@link BLOB_BINDINGS_FILE_VERSION}, `app` is the app slug, and `buckets` maps each logical name
* the fixture binds to its {@link BlobBindingsFileBucket}, 1 to `BLOB_BUCKETS_PER_APP_MAX` entries.
* A reader refuses any other version, an unknown key at any depth and a malformed entry; it never
* repairs one. Strict.
*/
const blobBindingsFileSchema = z.object({
	version: z.literal(1),
	app: appSlugSchema,
	buckets: bucketMap(blobBindingsFileBucketSchema)
}).strict();
/**
* Resolve the logical bucket `logical` for the DP-local org `orgId` in `file`. The one lookup rule of the
* SDK handles (WS-6, WS-8, WS-9); `fixtures/blob-bindings/cases.json` holds the same rule as data.
*
* 1. `logical` is not an own key of `file.buckets`: `not-declared`. An inherited name such as
*    `constructor` is not an own key.
* 2. The bucket has scope `global`: `bound` with its `global` binding, whatever `orgId` is.
* 3. `orgId` is `null`: `org-required`.
* 4. `orgId` is not an own key of the bucket's `orgs`: `org-not-bound`.
* 5. Otherwise `bound` with that org's binding.
*
* `file` must be the output of {@link blobBindingsFileSchema}. Pure; never throws.
*/
function resolveBlobBinding(file, logical, orgId) {
	const bucket = Object.hasOwn(file.buckets, logical) ? file.buckets[logical] : void 0;
	if (bucket === void 0) return { kind: "not-declared" };
	if (bucket.scope === "global") return {
		kind: "bound",
		mode: bucket.mode,
		physical: bucket.global
	};
	if (orgId === null) return { kind: "org-required" };
	const physical = Object.hasOwn(bucket.orgs, orgId) ? bucket.orgs[orgId] : void 0;
	if (physical === void 0) return { kind: "org-not-bound" };
	return {
		kind: "bound",
		mode: bucket.mode,
		physical
	};
}
//#endregion
export { APP_BLOB_BINDINGS_MAX_BYTES, BLOB_BINDINGS_FILE_ENV, BLOB_BINDINGS_FILE_VERSION, BLOB_BINDINGS_MOUNT_PATH, BLOB_BINDINGS_ORGS_MAX, BLOB_BUCKET_ENDPOINT_MAX_LENGTH, BLOB_BUCKET_ENDPOINT_PATTERN, BLOB_BUCKET_PROVIDERS, appBlobBindingsSchema, appBlobBucketBindingSchema, blobBindingsFileBucketSchema, blobBindingsFileSchema, blobBucketPhysicalSchema, resolveBlobBinding };

//# sourceMappingURL=blob-bindings.js.map